import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language, defaultLanguage } from '../lib/i18n';
import { updateMetaTags } from '../lib/meta';
import { useNavigate, useLocation } from 'react-router-dom';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isInitialized: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  language: defaultLanguage,
  setLanguage: () => {},
  isInitialized: false,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [language, setLanguageState] = useState<Language>(defaultLanguage);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    const hostname = window.location.hostname;

    let effectiveLang: Language;

    // Determine the language based on URL, hostname, or default.
    if (langParam === 'de' || langParam === 'ja') {
      effectiveLang = langParam;
    } else if (hostname.endsWith('.de')) {
      effectiveLang = 'de';
    } else {
      effectiveLang = defaultLanguage;
    }

    // Set the language state immediately.
    setLanguageState(effectiveLang);
    updateMetaTags(effectiveLang);

    // If the URL doesn't match the effective language, update it.
    // This handles the redirect from .de or landing on the root without a param.
    if (params.get('lang') !== effectiveLang) {
      params.set('lang', effectiveLang);
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    }

    // Signal that we are ready to render the app.
    setIsInitialized(true);
    
  }, []); // The empty dependency array is crucial. This runs only ONCE.

  const setLanguage = (newLang: Language) => {
    // This function is for user-driven language changes AFTER initialization.
    setLanguageState(newLang);
    updateMetaTags(newLang);

    const params = new URLSearchParams(location.search);
    params.set('lang', newLang);
    
    if (location.pathname.startsWith('/blog/')) {
        navigate({
          pathname: '/blog',
          search: params.toString()
        });
    } else {
        navigate({
          pathname: location.pathname,
          search: params.toString()
        });
    }
  };

  // Do not render the main application until the language has been determined.
  return (
    <LanguageContext.Provider value={{ language, setLanguage, isInitialized }}>
      {isInitialized ? children : null}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
